import { useState, useRef, useEffect, useCallback } from 'react';
import { Search, Loader2 } from 'lucide-react';
import { useDebouncedSearch } from '../../hooks/useSearch';
import SearchResults from './SearchResults';

interface SearchBarProps {
  /** Placeholder text for the input */
  placeholder?: string;
  /** Called when a result is selected */
  onResultSelect?: () => void;
}

/**
 * Search bar component with dropdown results
 * Supports keyboard shortcut Cmd/Ctrl + K to focus
 */
export default function SearchBar({
  placeholder = 'Search tasks and pages...',
  onResultSelect,
}: SearchBarProps) {
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    query,
    setQuery,
    results,
    isSearching,
    isIndexReady,
    isLoading,
  } = useDebouncedSearch(300);

  // Handle keyboard shortcut (Cmd/Ctrl + K)
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setIsOpen(true);
      }

      // Close on escape
      if (e.key === 'Escape') {
        setIsOpen(false);
        inputRef.current?.blur();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle input focus
  const handleFocus = useCallback(() => {
    setIsOpen(true);
  }, []);

  // Handle input change
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setQuery(e.target.value);
      if (!isOpen) setIsOpen(true);
    },
    [setQuery, isOpen]
  );

  // Handle result selection
  const handleSelect = useCallback(() => {
    setIsOpen(false);
    setQuery('');
    onResultSelect?.();
  }, [setQuery, onResultSelect]);

  // Determine if we should show results dropdown
  const showResults = isOpen && (query.trim().length > 0 || isLoading);

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        {/* Search icon or loading spinner */}
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" aria-hidden="true">
          {isSearching || isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Search className="w-4 h-4" />
          )}
        </div>

        {/* Search input */}
        <input
          ref={inputRef}
          type="search"
          value={query}
          onChange={handleChange}
          onFocus={handleFocus}
          placeholder={placeholder}
          className="w-full max-w-[33.6rem] pl-9 pr-12 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:bg-white dark:focus:bg-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus:border-transparent transition-colors min-h-[44px]"
          aria-label="Search tasks and pages"
          aria-expanded={showResults}
          aria-controls="search-results"
          role="combobox"
          aria-autocomplete="list"
          aria-busy={isSearching}
        />

        {/* Keyboard shortcut hint */}
        <div className="absolute right-3 top-1/2 -translate-y-1/2 hidden sm:flex items-center gap-1">
          <kbd className="px-1.5 py-0.5 text-xs font-mono text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-600 border border-gray-200 dark:border-gray-500 rounded">
            {navigator.platform.includes('Mac') ? 'Cmd' : 'Ctrl'}
          </kbd>
          <kbd className="px-1.5 py-0.5 text-xs font-mono text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-600 border border-gray-200 dark:border-gray-500 rounded">
            K
          </kbd>
        </div>
      </div>

      {/* Results dropdown */}
      {showResults && (
        <div
          id="search-results"
          className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 max-h-96 overflow-auto"
        >
          <SearchResults
            results={results}
            query={query}
            isLoading={isLoading}
            isIndexReady={isIndexReady}
            onSelect={handleSelect}
          />
        </div>
      )}
    </div>
  );
}
